/**
 * Instructor Service - Utility functions for managing instructors
 */

/**
 * Update an instructor by ID with the provided data
 * @param {number|string} instructorId - The ID of the instructor to update
 * @param {Object} instructorData - The data to update the instructor with
 * @returns {Promise<Object>} - The updated instructor data
 */
export const updateInstructor = async (instructorId, instructorData) => {
  try {
    // Get the authentication token from localStorage
    const token = localStorage.getItem('token');
    
    // Make sure languages is properly formatted
    if (instructorData.languages && Array.isArray(instructorData.languages)) {
      // Ensure there are no nested quotes in the array items
      instructorData.languages = instructorData.languages.map(lang => {
        if (typeof lang === 'string') {
          return lang.replace(/^"|"$/g, ''); // Remove surrounding quotes if present
        }
        return lang;
      });
    }
    
    // Convert Proxy object to plain object if needed
    const plainData = JSON.parse(JSON.stringify(instructorData));
    
    console.log('Updating instructor with data:', plainData);
    
    // Make the API request
    const response = await fetch(`https://api.kewaar.com/api/admin/instructors/${instructorId}`, {
      method: 'PUT',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(plainData)
    });

    // Check if the response is successful
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Server error response:', errorData);
      throw new Error(errorData.message || `Failed to update instructor with ID ${instructorId}`);
    }
    
    // Parse and return the response
    const result = await response.json();
    console.log('Instructor updated successfully:', result);
    return result;
    
  } catch (error) {
    console.error('Error updating instructor:', error);
    throw error;
  }
};

/**
 * Fetch an instructor by ID
 * @param {number|string} instructorId - The ID of the instructor to fetch
 * @returns {Promise<Object>} - The instructor data
 */
export const getInstructorById = async (instructorId) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`https://api.kewaar.com/api/admin/instructors/${instructorId}`, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Server error response:', errorData);
      throw new Error(errorData.message || `Failed to fetch instructor with ID ${instructorId}`);
    }

    const result = await response.json();
    return result.data;
    
  } catch (error) {
    console.error(`Error fetching instructor with ID ${instructorId}:`, error);
    throw error;
  }
};

/**
 * Create a sample instructor update payload with general fields
 * @returns {Object} A sample instructor payload with empty/default values
 */
export const createInstructorTemplate = () => {
  return {
    first_name: '',
    last_name: '',
    bio: '',
    bio_ar: '',
    expertise: '',
    expertise_ar: '',
    price_per_session: '',
    session_duration: 0,
    languages: [],
    is_available: true,
    timezone: '',
    specialties: []
  };
};

/**
 * Create a sample instructor payload based on the example provided
 * @returns {Object} A sample instructor update payload
 */
export const getSampleInstructorPayload = () => {
  return {
    "first_name": "[Instructor First Name]",
    "last_name": "[Instructor Last Name]",
    "email": "[Instructor Email]",
    "img_url": "[Profile Image URL]",
    "bio": "<p>[Instructor Bio in English - HTML Format]</p>",
    "bio_ar": "<p>[Instructor Bio in Arabic - HTML Format]</p>",
    "expertise": "[Instructor Expertise in English]",
    "expertise_ar": "[Instructor Expertise in Arabic]",
    "giving_sessions": 1,
    "video_url": "[Instructor Video URL]",
    "price_per_session": "50.00",
    "session_duration": 60,
    "languages": ["English", "Arabic"],
    "specialties": ["Specialty 1", "Specialty 2"],
    "is_available": 1,
    "timezone": "UTC+3"
  };
}; 